import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, User, Clock, Smartphone, Tablet, Monitor, Eye } from "lucide-react";
import { format } from "date-fns";
import { renderBlogContent } from "@/lib/blogMarkdown";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  cover_image: string | null;
  author: string;
  published: boolean;
  created_at: string;
  view_count?: number;
}

interface BlogPreviewModalProps {
  post: BlogPost | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Viewport = "mobile" | "tablet" | "desktop";

const viewportConfig: Record<Viewport, { width: string; icon: typeof Smartphone; label: string }> = {
  mobile: { width: "375px", icon: Smartphone, label: "Mobile" },
  tablet: { width: "768px", icon: Tablet, label: "Tablet" },
  desktop: { width: "100%", icon: Monitor, label: "Desktop" },
};

const BlogPreviewModal = ({ post, open, onOpenChange }: BlogPreviewModalProps) => {
  const [viewport, setViewport] = useState<Viewport>("desktop");

  if (!post) return null;

  const wordCount = post.content.split(/\s+/).length;
  const readTime = Math.max(1, Math.round(wordCount / 220));
  const vp = viewportConfig[viewport];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] overflow-hidden p-0 flex flex-col">
        <DialogHeader className="p-4 pb-0 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DialogTitle className="text-lg">Post Preview</DialogTitle>
              <Badge variant={post.published ? "default" : "secondary"}>
                {post.published ? "Published" : "Draft"}
              </Badge>
            </div>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              {(Object.entries(viewportConfig) as [Viewport, typeof vp][]).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <Button
                    key={key}
                    variant={viewport === key ? "default" : "ghost"}
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setViewport(key)}
                    title={cfg.label}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </Button>
                );
              })}
            </div>
          </div>
        </DialogHeader>

        {/* Preview container with viewport simulation */}
        <div className="flex-1 min-h-0 p-4 bg-muted/30">
          <div className="mx-auto h-full max-w-full transition-all duration-300" style={{ width: vp.width, maxWidth: "100%" }}>
            <div className="h-full bg-background rounded-lg border shadow-sm overflow-y-auto overflow-x-hidden">
              <div className="p-6">
                {post.cover_image && (
                  <div className="rounded-lg overflow-hidden mb-6 -mx-6 -mt-6">
                    <img
                      src={post.cover_image}
                      alt={post.title}
                      className="w-full h-48 sm:h-64 object-cover"
                    />
                  </div>
                )}

                <h1 className="font-display text-2xl sm:text-3xl font-bold mb-3">{post.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
                  <span className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" /> {post.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" /> {format(new Date(post.created_at), "d MMMM yyyy")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> {readTime} min read
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" /> {post.view_count ?? 0} view{(post.view_count ?? 0) !== 1 ? "s" : ""}
                  </span>
                </div>

                {post.excerpt && (
                  <p className="text-muted-foreground italic border-l-2 border-primary/30 pl-4 mb-6">
                    {post.excerpt}
                  </p>
                )}

                <div className="prose-sm">
                  {renderBlogContent(post.content)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BlogPreviewModal;
